import sherlockImg from '../assets/blog_sherlock.png';
import entertainmentImg from '../assets/blog_entertainment.png';
import celebrationImg from '../assets/blog_celebration.png';
import theatreImg from '../assets/blog_theatre.png';
import thrillerImg from '../assets/blog_thriller.png';

const blogData = [
  {
    slug: 'playing-sherlock-for-real',
    title: 'Playing Sherlock — For Real',
    subtitle: 'What happens when the magnifying glass is in your hands and the mystery is unfolding around you.',
    category: 'Experiences',
    date: 'May 2026',
    readTime: '6 min read',
    heroImage: sherlockImg,
    heroAlt: 'A detective desk with magnifying glass and candlelight casting golden shadows in a dark study',
    excerpt: 'Every Sherlock Holmes fan has imagined it — stepping into 221B Baker Street, scanning a crime scene, and piecing together clues that everyone else missed. But what if you could actually do it?',
    metaTitle: 'Playing Sherlock For Real — Mystery Mansion Blog',
    metaDescription: 'Discover what it feels like to step into a real-life mystery. From scanning crime scenes to piecing together clues, Mystery Mansion brings the detective fantasy to life.',
    content: [
      {
        type: 'paragraph',
        text: 'Every Sherlock Holmes fan has imagined it — stepping into 221B Baker Street, scanning a crime scene, and piecing together clues that everyone else missed. The deerstalker cap, the dramatic violin, the triumphant "Elementary, my dear Watson." It\'s a fantasy that\'s lived in books, movies, and our imaginations for over a century.'
      },
      {
        type: 'paragraph',
        text: 'But what if you could actually do it? Not in a VR headset. Not in a board game. In real life — with real stakes, real tension, and a mystery unfolding around you in a mansion you\'re staying in for the night.'
      },
      {
        type: 'heading',
        text: 'The Gap Between Watching and Doing'
      },
      {
        type: 'paragraph',
        text: 'There\'s a reason detective fiction has endured for centuries. From Arthur Conan Doyle to Agatha Christie, from CID to Sacred Games — we\'re wired to solve puzzles. Our brains release dopamine when we spot patterns, make connections, and arrive at conclusions before others do.'
      },
      {
        type: 'paragraph',
        text: 'But watching a detective solve a case on screen and actually being the one crouched over a clue, debating theories with your friends at 2 AM, heart racing because you just realized the answer was in front of you all along — those are fundamentally different experiences.'
      },
      {
        type: 'quote',
        text: '"The world is full of obvious things which nobody by any chance ever observes." — Sherlock Holmes, The Hound of the Baskervilles'
      },
      {
        type: 'heading',
        text: 'What Makes a Real-Life Mystery Different'
      },
      {
        type: 'paragraph',
        text: 'Escape rooms tried to capture this feeling, and to their credit, they partially succeeded. But they\'re limited — you\'re locked in a small room, you know it\'s a game, and the \'mystery\' is really just a series of combination locks.'
      },
      {
        type: 'paragraph',
        text: 'A real immersive mystery is different. It doesn\'t announce itself. It doesn\'t give you a briefing packet. You check into a stunning property with your friends, expecting a great time — and then something happens. Something that pulls you into a narrative so compelling that you forget you signed up for it.'
      },
      {
        type: 'list',
        items: [
          'No locked rooms — the entire property is your crime scene',
          'No time pressure countdown — the story unfolds naturally',
          'No game master walking you through it — you\'re on your own',
          'The clues are woven into the environment, the conversations, the details you might overlook',
          'Your group dynamics become part of the story'
        ]
      },
      {
        type: 'heading',
        text: 'The Psychology of Playing Detective'
      },
      {
        type: 'paragraph',
        text: 'When you\'re placed in a real mystery scenario, something fascinating happens to group dynamics. The quiet friend becomes the sharpest observer. The loudest person in the room starts second-guessing themselves. Natural leaders emerge, alliances form, and everyone discovers a side of themselves they didn\'t know existed.'
      },
      {
        type: 'paragraph',
        text: 'It\'s not just entertainment — it\'s a mirror. And that\'s what makes it unforgettable.'
      },
      {
        type: 'heading',
        text: 'Why This Matters Now'
      },
      {
        type: 'paragraph',
        text: 'In an age of infinite scrolling and passive consumption, there\'s a growing hunger for experiences that demand your full attention. Experiences that make you think, collaborate, and feel genuinely alive. The detective fantasy isn\'t just about solving crimes — it\'s about engaging with the world in a way that screens can\'t replicate.'
      },
      {
        type: 'paragraph',
        text: 'So the next time you re-watch Knives Out or binge a true crime podcast, ask yourself: what if you could step inside the story? Because now, you can.'
      }
    ]
  },
  {
    slug: 'how-indian-entertainment-is-evolving',
    title: 'How Indian Entertainment is Evolving',
    subtitle: 'From Bollywood blockbusters to immersive experiences — the entertainment landscape is shifting.',
    category: 'Culture',
    date: 'May 2026',
    readTime: '7 min read',
    heroImage: entertainmentImg,
    heroAlt: 'A grand Indian theatre with ornate golden architecture and dramatic spotlights cutting through fog',
    excerpt: 'India\'s entertainment industry is undergoing its most exciting transformation in decades. Beyond OTT and multiplexes, a new category is emerging — one that puts you inside the story.',
    metaTitle: 'How Indian Entertainment is Evolving — Mystery Mansion Blog',
    metaDescription: 'Explore how India\'s entertainment industry is shifting from passive consumption to immersive experiences, and why experiential entertainment is the next frontier.',
    content: [
      {
        type: 'paragraph',
        text: 'India\'s entertainment industry is worth over $28 billion. For decades, it was synonymous with one thing: Bollywood. Then came television. Then OTT platforms. Each wave fundamentally changed how Indians consumed stories. But the next wave isn\'t about watching stories at all — it\'s about living them.'
      },
      {
        type: 'heading',
        text: 'The OTT Revolution Was Just the Beginning'
      },
      {
        type: 'paragraph',
        text: 'When Netflix, Amazon Prime, and Hotstar entered India, they didn\'t just offer more content — they changed the language of Indian storytelling. Suddenly, we had Sacred Games, Paatal Lok, and Scam 1992. Stories became grittier, more nuanced, and audiences became more sophisticated.'
      },
      {
        type: 'paragraph',
        text: 'But here\'s the thing about sophisticated audiences: they get bored faster. They crave novelty. And the most novel thing you can do with a story is put the audience inside it.'
      },
      {
        type: 'heading',
        text: 'The Experience Economy Arrives in India'
      },
      {
        type: 'paragraph',
        text: 'Globally, the "experience economy" has been a megatrend for over a decade. In the West, immersive theatre (like Sleep No More in New York), murder mystery dinners, and interactive entertainment experiences have been thriving. India is now catching up — and in some ways, leapfrogging.'
      },
      {
        type: 'quote',
        text: '"People don\'t want more things. They want more stories to tell. The businesses that understand this will define the next decade of entertainment."'
      },
      {
        type: 'paragraph',
        text: 'Think about it: Indian consumers, especially millennials and Gen Z in metros, are spending less on products and more on experiences. Weekend getaways, unique dining concepts, adventure activities, and now — immersive mystery experiences that combine luxury stays with interactive storytelling.'
      },
      {
        type: 'heading',
        text: 'Why India is Uniquely Positioned'
      },
      {
        type: 'list',
        items: [
          'A storytelling culture that goes back thousands of years',
          'A massive young population hungry for novel experiences',
          'Rising disposable incomes in tier 1 and tier 2 cities',
          'A booming luxury hospitality sector with stunning properties',
          'Social media culture that amplifies unique experiences'
        ]
      },
      {
        type: 'paragraph',
        text: 'India doesn\'t need to import the immersive entertainment format — it needs to make it its own. And that means weaving in elements that resonate with Indian audiences: family dynamics, social hierarchies, the thrill of collective problem-solving, and stories rooted in settings that feel familiar yet extraordinary.'
      },
      {
        type: 'heading',
        text: 'What\'s Next'
      },
      {
        type: 'paragraph',
        text: 'The next five years will see experiential entertainment become as mainstream as going to the movies. Mystery experiences, interactive theatre, and narrative-driven hospitality will create an entirely new category — one that exists at the intersection of travel, entertainment, and luxury.'
      },
      {
        type: 'paragraph',
        text: 'The question isn\'t whether this shift will happen. It\'s whether you\'ll be watching from the sidelines — or stepping into the story.'
      }
    ]
  },
  {
    slug: 'most-unique-way-to-celebrate-in-ncr',
    title: 'Most Unique Way to Celebrate in NCR',
    subtitle: 'Forget the same old house parties and overpriced restaurants. Here\'s something your group will actually remember.',
    category: 'Entertainment',
    date: 'Apr 2026',
    readTime: '5 min read',
    heroImage: celebrationImg,
    heroAlt: 'A luxurious celebration in a mysterious mansion with golden fairy lights and champagne glasses',
    excerpt: 'We\'ve all been there — the group chat buzzing about someone\'s birthday, and everyone throwing out the same tired suggestions. What if there was an option that made everyone put their phones down?',
    metaTitle: 'Most Unique Way to Celebrate in NCR — Mystery Mansion Blog',
    metaDescription: 'Looking for unique celebration ideas in Delhi NCR? Discover how a mystery mansion experience combines luxury stays with interactive entertainment for unforgettable parties.',
    content: [
      {
        type: 'paragraph',
        text: 'We\'ve all been there. The group chat buzzes about someone\'s birthday, anniversary, or "let\'s just do something fun" plan. The suggestions come in: a lounge in Hauz Khas, a farmhouse in Chattarpur, bowling at Ambience Mall, or — the nuclear option — a road trip to Jaipur that half the group will bail on.'
      },
      {
        type: 'paragraph',
        text: 'Nothing wrong with any of those. But be honest — when was the last time you came back from a celebration and thought, "That was genuinely unforgettable"?'
      },
      {
        type: 'heading',
        text: 'The Problem with Standard Celebrations'
      },
      {
        type: 'paragraph',
        text: 'Most celebrations follow a predictable arc: meet up, eat, drink, take photos, go home. The venue changes, but the experience doesn\'t. And in the age of Instagram, even the most aesthetic venue becomes background noise — because we\'ve seen it all before.'
      },
      {
        type: 'paragraph',
        text: 'What\'s missing isn\'t a better venue or fancier food. What\'s missing is a shared experience that bonds the group — something that makes everyone genuinely present, engaged, and part of something bigger than a dinner reservation.'
      },
      {
        type: 'quote',
        text: '"The best celebrations aren\'t about the place. They\'re about the story you create together."'
      },
      {
        type: 'heading',
        text: 'Enter: The Mystery Celebration'
      },
      {
        type: 'paragraph',
        text: 'Imagine this: you check into a gorgeous premium villa with your group. The vibe is perfect — pool, music, great food, drinks flowing. It feels like the best house party ever. And then... something happens. Something unexpected. Something that turns your celebration into an adventure.'
      },
      {
        type: 'paragraph',
        text: 'Suddenly, you\'re not just partying — you\'re solving a mystery together. The birthday person becomes the lead detective. The quiet friend spots a crucial clue. The entire group is laughing, debating, and experiencing something none of them have ever done before.'
      },
      {
        type: 'heading',
        text: 'Why Groups Love It'
      },
      {
        type: 'list',
        items: [
          'It works for birthdays, bachelor/bachelorette parties, anniversaries, and "just because" weekends',
          'Group sizes of 6–15 people are ideal — perfect for friend circles',
          'Everyone participates — no one\'s sitting in a corner on their phone',
          'You get a luxury stay AND a unique experience in one package',
          'The stories from the night become legendary in your friend group'
        ]
      },
      {
        type: 'heading',
        text: 'Available in Gurugram and Greater Noida'
      },
      {
        type: 'paragraph',
        text: 'Whether your group is based in Delhi, Noida, Gurgaon, or Faridabad — there\'s a Mystery Mansion within easy reach. Premium properties in Gurugram and Greater Noida offer the perfect setting for a celebration that\'s equal parts luxury and adventure.'
      },
      {
        type: 'paragraph',
        text: 'Next time the group chat lights up with "let\'s plan something," you know exactly what to suggest. And trust us — no one\'s going to counter with "how about bowling instead."'
      }
    ]
  },
  {
    slug: 'interactive-theatre-meets-staycation',
    title: 'Interactive Theatre Meets Staycation',
    subtitle: 'What happens when you merge immersive performance art with a luxury overnight stay? Something entirely new.',
    category: 'Experiences',
    date: 'Apr 2026',
    readTime: '6 min read',
    heroImage: theatreImg,
    heroAlt: 'An immersive theatre scene in a mysterious mansion with dramatic lighting and fog',
    excerpt: 'Interactive theatre has been transforming global entertainment for years. But combining it with a luxury staycation creates something entirely unprecedented — a new genre of experience.',
    metaTitle: 'Interactive Theatre Meets Staycation — Mystery Mansion Blog',
    metaDescription: 'Discover the new genre of entertainment that combines immersive interactive theatre with luxury staycations. Mystery Mansion is pioneering this format in India.',
    content: [
      {
        type: 'paragraph',
        text: 'In 2011, a show called Sleep No More opened in a converted warehouse in Manhattan. Audiences wore masks, wandered freely through a multi-floor set, and experienced a retelling of Macbeth through dance, physical theatre, and environmental storytelling. There was no stage. No seats. No fourth wall. It changed theatre forever.'
      },
      {
        type: 'paragraph',
        text: 'Fourteen years later, the immersive theatre movement has spawned hundreds of shows worldwide. But almost none of them have taken the next logical step: what if the experience lasted not 90 minutes, but an entire night? What if the venue wasn\'t a warehouse, but a stunning property you\'re actually staying in?'
      },
      {
        type: 'heading',
        text: 'The Staycation Revolution'
      },
      {
        type: 'paragraph',
        text: 'Simultaneously, the staycation trend has been exploding — especially post-COVID. People discovered that you don\'t need to fly to Bali for a memorable getaway. A beautiful property an hour from your city, with the right group of people, can be transformative.'
      },
      {
        type: 'paragraph',
        text: 'But staycations have a ceiling. You can only take so many pool photos before the experience starts to feel formulaic. The property is gorgeous, the food is great, but by evening, everyone\'s on their phones, wondering what to do next.'
      },
      {
        type: 'quote',
        text: '"The magic happens when you take two proven concepts — immersive theatre and luxury hospitality — and realize they were always meant to be combined."'
      },
      {
        type: 'heading',
        text: 'The Hybrid Format'
      },
      {
        type: 'paragraph',
        text: 'Mystery Mansion sits at the intersection of these two worlds. It\'s a luxury staycation where a mystery unfolds around you — complete with characters, plot twists, and clues woven into the property itself. You\'re not an audience member watching actors perform. You\'re a participant in a story that responds to your actions.'
      },
      {
        type: 'list',
        items: [
          'The property IS the stage — every room, every corner is part of the narrative',
          'There are no actors breaking character — the immersion never breaks',
          'The mystery adapts to your group\'s pace and style',
          'You solve it together, at your own rhythm, through the night',
          'The luxury stay (pool, food, drinks) happens around and within the mystery'
        ]
      },
      {
        type: 'heading',
        text: 'Why This Format is the Future'
      },
      {
        type: 'paragraph',
        text: 'Traditional entertainment asks you to sit and watch. Immersive entertainment asks you to stand and watch from closer. This format asks you to live inside the story — while also having the best night out with your friends.'
      },
      {
        type: 'paragraph',
        text: 'It\'s not theatre. It\'s not a hotel. It\'s not a game. It\'s a new category entirely — and once you experience it, everything else feels flat.'
      },
      {
        type: 'heading',
        text: 'Coming to a City Near You'
      },
      {
        type: 'paragraph',
        text: 'Currently available in Gurugram and Greater Noida, with more locations launching soon. The future of entertainment isn\'t something you watch — it\'s something you check into.'
      }
    ]
  },
  {
    slug: 'for-thriller-novel-readers-this-ones-for-you',
    title: 'For Thriller Novel Readers — This One\'s For You',
    subtitle: 'You\'ve devoured every Agatha Christie. Binged every true crime podcast. Now it\'s your turn to step inside the pages.',
    category: 'Entertainment',
    date: 'Mar 2026',
    readTime: '5 min read',
    heroImage: thrillerImg,
    heroAlt: 'Old thriller books stacked on an antique wooden table with candlelight and a glass of whiskey',
    excerpt: 'If you\'re the kind of person who finishes a thriller novel at 3 AM and immediately wants to start another, we built something for you. Something that makes the stories jump off the page.',
    metaTitle: 'For Thriller Novel Readers — Mystery Mansion Blog',
    metaDescription: 'A love letter to thriller and mystery novel readers. If you\'ve read every Agatha Christie and devoured true crime podcasts, Mystery Mansion is the experience you\'ve been waiting for.',
    content: [
      {
        type: 'paragraph',
        text: 'You know who you are. You\'re the one who figured out the twist in Gone Girl before page 200. You\'ve read And Then There Were None at least twice. Your Spotify Wrapped has a suspicious amount of true crime podcasts. When someone says "whodunit," your ears perk up like a golden retriever hearing a treat bag.'
      },
      {
        type: 'paragraph',
        text: 'This one\'s for you.'
      },
      {
        type: 'heading',
        text: 'The Reader\'s Curse'
      },
      {
        type: 'paragraph',
        text: 'Every serious thriller reader knows the curse: the better you get at reading mysteries, the harder it becomes to be genuinely surprised. You\'ve learned the tropes. You know the unreliable narrator is unreliable by chapter three. You can spot the red herring before the author plants it.'
      },
      {
        type: 'paragraph',
        text: 'And that\'s frustrating — because the whole point of a mystery is the thrill of not knowing. The delicious tension of "who did it?" The satisfaction of connecting dots. But when you\'ve read hundreds of them, the pattern recognition kicks in and the magic fades.'
      },
      {
        type: 'quote',
        text: '"A really good detective never gets married." — Agatha Christie. We can\'t help with your love life, but we can give you a mystery worthy of your skills.'
      },
      {
        type: 'heading',
        text: 'Why a Real Mystery Hits Different'
      },
      {
        type: 'paragraph',
        text: 'Here\'s what no novel, no matter how brilliantly written, can replicate: the physicality of a real mystery. The act of walking into a room and noticing something that\'s slightly off. The sound of footsteps that shouldn\'t be there. The weight of an envelope you weren\'t expecting. The look on your friend\'s face when they realize something you haven\'t.'
      },
      {
        type: 'paragraph',
        text: 'In a book, the author controls what you see. In a real mystery, you control where you look. And that changes everything.'
      },
      {
        type: 'heading',
        text: 'Your Reading Skills Become Superpowers'
      },
      {
        type: 'list',
        items: [
          'Pattern recognition from years of reading? You\'ll spot clues others miss',
          'Understanding of narrative structure? You\'ll anticipate plot twists',
          'Attention to detail honed by Christie and Conan Doyle? Every observation matters',
          'That instinct for "something\'s not right here"? Trust it — it\'s real this time',
          'Your ability to construct theories? You\'ll lead the investigation'
        ]
      },
      {
        type: 'heading',
        text: 'The Ultimate Book Club Activity'
      },
      {
        type: 'paragraph',
        text: 'Forget discussing the latest release over wine and cheese. Imagine your book club — the people who\'ve spent years debating fictional mysteries together — actually solving one. In a mansion. Overnight. With all the atmosphere, tension, and twists of your favorite novels, but this time, you\'re the protagonist.'
      },
      {
        type: 'paragraph',
        text: 'We\'ve seen thriller readers walk in confident and walk out humbled. We\'ve seen them walk in skeptical and walk out as our biggest evangelists. Because no matter how many books you\'ve read, nothing prepares you for the moment the mystery becomes real.'
      },
      {
        type: 'heading',
        text: 'Your Next Chapter Starts Here'
      },
      {
        type: 'paragraph',
        text: 'You\'ve spent years turning pages. It\'s time to step inside them. Mystery Mansion is available in Gurugram and Greater Noida — and the story is waiting for you.'
      }
    ]
  }
];

export default blogData;
